import { isGrafanaFaroEnabled } from '@/lib/grafana-faro-enabled';

type FaroClient = {
  api: {
    pushEvent: (
      name: string,
      attributes: Record<string, string>,
      domain?: string,
    ) => void;
    setUser: (user: {
      id?: string;
      email?: string;
      username?: string;
      attributes?: Record<string, string>;
    }) => void;
  };
};

let faroInstance: FaroClient | null = null;
let initPromise: Promise<FaroClient | null> | null = null;

function trimTrailingSlashes(value: string): string {
  let end = value.length;
  while (end > 0 && value.codePointAt(end - 1) === 47) end--;
  return end === value.length ? value : value.slice(0, end);
}

function getApiHost(): string {
  return trimTrailingSlashes(process.env.NEXT_PUBLIC_REST_API_ENDPOINT ?? '');
}

function buildPropagateTraceHeaderCorsUrls(): (string | RegExp)[] {
  const urls: (string | RegExp)[] = [];
  const apiHost = getApiHost();
  if (apiHost) urls.push(apiHost);
  if (urls.length === 0) urls.push(/qualidoc\.(life|com\.br)/);
  return urls;
}

function shouldIgnoreError(message: string | undefined): boolean {
  if (!message) return false;
  const m = message.toLowerCase();
  return (
    m.includes('script error') ||
    m.includes('resizeobserver loop') ||
    m.includes('non-error promise rejection') ||
    m.includes('sessão expirada') ||
    m.includes('session expired') ||
    m.includes('request failed with status code 401') ||
    m.includes('[next-auth][error][client_fetch_error]') ||
    m.includes('abort fetching component for route: "/logout"') ||
    m.includes('there was an error setting cookie `_pk_id')
  );
}

async function initializeGrafanaFaro(): Promise<FaroClient | null> {
  if (typeof window === 'undefined' || !isGrafanaFaroEnabled) {
    return null;
  }

  if (faroInstance) {
    return faroInstance;
  }

  const url = process.env.NEXT_PUBLIC_GRAFANA_FARO_URL;
  if (!url) {
    return null;
  }

  const appName =
    process.env.NEXT_PUBLIC_GRAFANA_FARO_APP_NAME ?? 'qualiapp-shop';
  const appEnv =
    process.env.NEXT_PUBLIC_GRAFANA_FARO_ENV ??
    process.env.NEXT_PUBLIC_APPLICATION_MODE ??
    'development';
  const appVersion =
    process.env.NEXT_PUBLIC_VERSION ?? process.env.npm_package_version;
  const bundleId = process.env.NEXT_PUBLIC_GRAFANA_FARO_BUNDLE_ID;

  try {
    const [{ initializeFaro, getWebInstrumentations }, { TracingInstrumentation }] =
      await Promise.all([
        import('@grafana/faro-web-sdk'),
        import('@grafana/faro-web-tracing'),
      ]);

    faroInstance = initializeFaro({
      url,
      app: {
        name: appName,
        version: appVersion,
        environment: appEnv,
        bundleId,
      },
      instrumentations: [
        ...getWebInstrumentations({
          captureConsole: true,
        }),
        new TracingInstrumentation({
          instrumentationOptions: {
            propagateTraceHeaderCorsUrls: buildPropagateTraceHeaderCorsUrls(),
          },
        }),
      ],
      beforeSend: (item) => {
        if (item.type === 'exception') {
          const payload = item.payload as { value?: string };
          if (shouldIgnoreError(payload?.value)) {
            return null;
          }
        }
        return item;
      },
      sessionTracking: {
        enabled: true,
        samplingRate: Number(
          process.env.NEXT_PUBLIC_GRAFANA_FARO_SESSION_SAMPLE_RATE ?? '1',
        ),
      },
    }) as FaroClient;

    return faroInstance;
  } catch (err) {
    console.error('[Faro] Failed to initialize Grafana RUM:', err);
    return null;
  }
}

export function grafanaFaroInit(): Promise<FaroClient | null> {
  if (!isGrafanaFaroEnabled) {
    return Promise.resolve(null);
  }

  if (!initPromise) {
    initPromise = initializeGrafanaFaro();
  }

  return initPromise;
}

export function getGrafanaFaro(): FaroClient | null {
  return faroInstance;
}

export function grafanaFaroSetUser(user: {
  id?: string;
  email?: string;
  username?: string;
  attributes?: Record<string, string>;
}): void {
  if (!faroInstance) return;
  faroInstance.api.setUser(user);
}
